import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getSocket } from '../lib/socket';
import { useToast } from '../components/ui/Toast';

/**
 * Workspace-scoped realtime fan-out. Mounted once in AppShell with the
 * active workspace id. Joins the gateway's `workspace:{id}` room (the
 * backend verifies membership) over the *shared* socket — it never opens
 * a second connection — and invalidates the React Query caches affected
 * by server-side mutations so the UI updates without a manual refresh:
 *
 *   - `ap_invoice.changed` → AP list + inbox counts (new invoice, OCR done)
 *   - `approval.changed`   → pending approvals queue (requested / decided)
 *   - `job.completed`      → same as the above + a subtle toast
 *
 * Payloads are id/kind-only; we always re-fetch from the API rather than
 * trusting the socket frame, so a missed event during a brief disconnect
 * self-heals on the next poll. On (re)connect we re-join the room so a
 * dropped socket re-subscribes automatically.
 */
export function useWorkspaceLiveEvents(wsId: string | undefined): void {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!wsId) return;
    const socket = getSocket();

    const join = () => socket.emit('workspace:join', { workspaceId: wsId });
    // Join now (if already connected) and on every (re)connect.
    if (socket.connected) join();
    socket.on('connect', join);

    function invalidateAp() {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    }
    function onApInvoiceChanged(payload: { id?: string }) {
      invalidateAp();
      if (payload?.id) {
        qc.invalidateQueries({ queryKey: ['ap-invoice', wsId, payload.id] });
      }
    }
    function onApprovalChanged() {
      qc.invalidateQueries({ queryKey: ['approvals', wsId] });
    }
    function onJobCompleted(payload: { kind?: string }) {
      // OCR completion already triggers ap_invoice.changed; the toast is
      // the only extra here, and only for jobs the user is likely waiting
      // on. Kept subtle + best-effort (spec: toasts are reserved, not chatty).
      if (payload?.kind === 'ocr') {
        invalidateAp();
        toast.info(t('liveEvents.jobCompleted'));
      }
    }

    socket.on('ap_invoice.changed', onApInvoiceChanged);
    socket.on('approval.changed', onApprovalChanged);
    socket.on('job.completed', onJobCompleted);

    return () => {
      socket.emit('workspace:leave', { workspaceId: wsId });
      socket.off('connect', join);
      socket.off('ap_invoice.changed', onApInvoiceChanged);
      socket.off('approval.changed', onApprovalChanged);
      socket.off('job.completed', onJobCompleted);
    };
  }, [wsId, qc, toast, t]);
}
